import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { cn } from '@/utils/cn';
import {
  BarChart3,
  ShoppingCart,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Package,
  Euro,
  Store,
  // MapPin,
  Users,
  User,
  FileText,
  X
} from 'lucide-react';
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher';
import { useAuthStore } from '@/stores/authStore';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { getMyProfile } from '@/services/clientService';
import { useChatStore } from '@/stores/chatStore';
import chatService from '@/services/chat';
import { useWebSocket } from '@/services/websocket';

const buyerNavigation = [
  { name: 'Tableau de Bord', href: '/client/dashboard', icon: BarChart3 },
  { name: 'Mon compte', href: '/client/settings', icon: User },
  { name: 'Points de Vente', href: '/client/sales-points', icon: Store },
  { name: 'Produits', href: '/client/catalog', icon: Package },
  { name: 'Bordereau de Prix', href: '/client/pricing', icon: Euro },
  { name: 'Commandes', href: '/client/orders', icon: ShoppingCart },
  { name: 'Documentation', href: '/client/documentation', icon: FileText },
  { name: 'Contact', href: '/client/members', icon: Users, buyerOnly: true },  // Seulement pour buyers (clients directs)
  // { name: 'Mes Adresses', href: '/client/addresses', icon: MapPin },
];

// Navigation spécifique aux centrales d'achat
const centralBuyerNavigation = [
  { name: 'Mes Clients', href: '/client/my-adherents', icon: Users, centralOnly: true },
];



interface ClientSidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function ClientSidebar({ mobileOpen = false, onMobileClose }: ClientSidebarProps) {
  const location = useLocation();
  const { logout } = useAuthStore();
  const { setUnreadCount, incrementUnread } = useChatStore();
  const [clientProfile, setBuyerProfile] = useState<any>(null);
  const [compact, setCompact] = useState<boolean>(() => {
    try { return localStorage.getItem('buyer_sidebar_compact') === '1'; } catch { return false; }
  });

  // WebSocket pour les notifications de chat
  useWebSocket((message: any) => {
    if (message.type === 'notification' && message.data?.kind === 'chat_new_message') {
      incrementUnread();
    }
  });

  // Charger le profil Client et le nombre de messages non lus
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const [profile, unreadData] = await Promise.all([
          getMyProfile(),
          chatService.getUnreadCount()
        ]);

        if (!cancelled) {
          setBuyerProfile(profile);
          setUnreadCount(unreadData.unread_count ?? 0);
        }
      } catch (error) {
        console.error('Erreur chargement données ClientSidebar:', error);
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [setUnreadCount]);

  // Auto-compact on small screens
  const [autoCompact, setAutoCompact] = useState<boolean>(false);
  useEffect(() => {
    const onResize = () => {
      setAutoCompact(window.innerWidth <= 1024);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const effectiveCompact = compact || autoCompact;

  useEffect(() => {
    try { localStorage.setItem('buyer_sidebar_compact', compact ? '1' : '0'); } catch { }
  }, [compact]);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const sidebarContent = (
    <div
      className={cn(
        'flex flex-col h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-all duration-300',
        // On mobile drawer: always expanded width
        mobileOpen ? 'w-64' : (effectiveCompact ? 'w-16' : 'w-64')
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b-[3px] border-green-700">
        <div className="flex items-center gap-2 h-full">
          <img
            src="/NOVA - Goutte ALLIANCE.png"
            alt="Nova logo"
            className="w-10 h-10 object-contain rounded-md bg-white"
          />
          {(!effectiveCompact || mobileOpen) && (
            <span className="text-lg font-semibold text-slate-800 dark:text-slate-100">
              NOVA
            </span>
          )}
        </div>
        {/* Close button on mobile, compact toggle on desktop */}
        <button
          onClick={mobileOpen ? onMobileClose : () => setCompact(!compact)}
          className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
        >
          {mobileOpen ? (
            <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          ) : effectiveCompact ? (
            <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Navigation standard */}
        {buyerNavigation.map((item) => {
          // Filtrer les items buyerOnly si l'utilisateur est un membre (quel que soit le type)
          if (item.buyerOnly && clientProfile?.member_type) {
            console.log(`🚫 Hiding menu "${item.name}" - User has member_type:`, clientProfile.member_type);
            return null;
          }
          if (item.buyerOnly && !clientProfile?.member_type) {
            console.log(`✅ Showing menu "${item.name}" - User is a Client (no member_type)`);
          }

          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = item.icon;

          const linkContent = (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg shadow-orange-500/30'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                (effectiveCompact && !mobileOpen) && 'justify-center'
              )}
            >
              <Icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-white')} />
              {(!effectiveCompact || mobileOpen) && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="font-medium">{item.name}</span>
                </div>
              )}
            </Link>
          );

          if (effectiveCompact && !mobileOpen) {
            return (
              <Tooltip key={item.name}>
                <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                <TooltipContent side="right">{item.name}</TooltipContent>
              </Tooltip>
            );
          }

          return linkContent;
        })}


        {/* Navigation spécifique centrales d'achat */}
        {(clientProfile?.client_type === 'central' || clientProfile?.client_type === 'centrale') && centralBuyerNavigation.map((item) => {
          const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
          const Icon = item.icon;

          const linkContent = (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-gradient-to-r from-orange-500 to-amber-600 text-white shadow-lg shadow-orange-500/30'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800',
                (effectiveCompact && !mobileOpen) && 'justify-center'
              )}
            >
              <Icon className={cn('h-5 w-5 flex-shrink-0', isActive && 'text-white')} />
              {(!effectiveCompact || mobileOpen) && (
                <div className="flex-1 flex items-center justify-between">
                  <span className="font-medium">{item.name}</span>
                </div>
              )}
            </Link>
          );

          if (effectiveCompact && !mobileOpen) {
            return (
              <Tooltip key={item.name}>
                <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                <TooltipContent side="right">{item.name}</TooltipContent>
              </Tooltip>
            );
          }

          return linkContent;
        })}
      </nav>

      {/* Footer - iOS safe area padding */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-2 pb-safe space-y-2" style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}>
        {/* Theme Switcher */}
        <div className={cn('flex', (effectiveCompact && !mobileOpen) ? 'justify-center' : 'px-2')}>
          <ThemeSwitcher showLabel={!effectiveCompact || mobileOpen} />
        </div>

        {/* Direct Logout - Enhanced touch target for mobile */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg border border-red-200 text-red-600 bg-white hover:bg-red-50 active:bg-red-100 dark:bg-slate-900 dark:border-red-400/40 dark:hover:bg-red-950/20 text-sm font-medium transition-colors min-h-[44px]"
        >
          <LogOut className="h-4 w-4" />
          {(!effectiveCompact || mobileOpen) && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop sidebar: always visible, hidden on mobile */}
      <div className="hidden md:block">
        {sidebarContent}
      </div>

      {/* Mobile drawer: overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <div className="relative z-10 h-full w-fit">
            {sidebarContent}
          </div>
        </div>
      )}
    </TooltipProvider>
  );
}
